import { Color, Spacing } from '@persona-id/design-tokens';
import {
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  LinkExternalIcon,
} from '@persona-id/design-tokens/icons';
import { motion } from 'framer-motion';
import pluralize from 'pluralize';
import { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom-v5-compat';
import styled, { css } from 'styled-components';

import BlueprintDemoModalContents from 'dashboard/components/BlueprintDemoModalContents';
import BlueprintsAllModal from 'dashboard/components/BlueprintsAllModal';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Tooltip from 'dashboard/components/Tooltip';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalContainer } from 'dashboard/layouts/ModalLayout';
import {
  Blueprint,
  BlueprintBlockType,
  BlueprintPreviewMap,
  BlueprintServerMetadata,
  IndustryName,
  UseCaseV2Name,
} from 'dashboard/models/blueprint';
import { Case } from 'dashboard/models/case';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { BuilderPreviewMap } from 'dashboard/models/inquiry-template';
import { Organization, OrganizationStatus } from 'dashboard/models/organization';
import { OrganizationPlan } from 'dashboard/models/organization-plan';
import { Report } from 'dashboard/models/report';
import { User } from 'dashboard/models/user';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { RootState } from 'dashboard/redux/store';
import BlueprintsQuizV2Modal from 'dashboard/views/DashboardSandboxHomeView/BlueprintsQuizV2Modal';
import ChooseIntegrationMethodStep from 'dashboard/views/DashboardSandboxHomeView/ChooseIntegrationMethodStep';
import ChooseSolutionStep from 'dashboard/views/DashboardSandboxHomeView/ChooseSolutionStep';
import {
  DashboardPageLink,
  guideContent,
  OnboardingGuideModuleKey,
  OnboardingGuideStep,
  OnboardingGuideStepKey,
} from 'dashboard/views/DashboardSandboxHomeView/constants';
import InviteTeamMembersStep from 'dashboard/views/DashboardSandboxHomeView/InviteTeamMembersStep';
import PreviewSolutionStep from 'dashboard/views/DashboardSandboxHomeView/PreviewSolutionStep';
import RequestProductionAccessStep from 'dashboard/views/DashboardSandboxHomeView/RequestProductionAccessStep';
import ReviewCaseStep from 'dashboard/views/DashboardSandboxHomeView/ReviewCaseStep';
import ReviewCompletedInquiryStep from 'dashboard/views/DashboardSandboxHomeView/ReviewCompletedInquiryStep';
import ReviewReportResultsStep from 'dashboard/views/DashboardSandboxHomeView/ReviewReportResultsStep';
import ReviewWorkflowRunStep from 'dashboard/views/DashboardSandboxHomeView/ReviewWorkflowRunStep';
import RunTestInquiryInProductionStep from 'dashboard/views/DashboardSandboxHomeView/RunTestInquiryInProductionStep';
import RunTestInquiryStep from 'dashboard/views/DashboardSandboxHomeView/RunTestInquiryStep';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';

interface DashboardSandboxHomeViewProps {
  previouslyUsedBlueprints: Blueprint[];
  blueprintsCatalogServer: BlueprintServerMetadata[];
  blueprintPreviews: BlueprintPreviewMap;
  casesForSelectedBlueprint: Case[];
  currentEnvironment: Environment;
  inquiriesForSelectedBlueprint: Inquiry[];
  inquiryTemplateBuilderPreviews: BuilderPreviewMap;
  organization: Organization;
  currentOrganizationPlan: OrganizationPlan;
  reportsForSelectedBlueprint: Report[];
  users: User[];
  workflowRunsForSelectedBlueprint: WorkflowRun[];
  isLoadingPreviouslyUsedBlueprints: boolean;
  isLoadingBlueprintsCatalog: boolean;
  isLoadingCasesForSelectedBlueprint: boolean;
  isLoadingInquiriesForSelectedBlueprint: boolean;
  isLoadingReportsForSelectedBlueprint: boolean;
  isLoadingUsers: boolean;
  isLoadingWorkflowRunsForSelectedBlueprint: boolean;
  isSavingCompletedStep: boolean;
  blueprintsQuizV2SelectedIndustry: IndustryName;
  blueprintsQuizV2SelectedUseCase: UseCaseV2Name;
  blueprintsQuizV2ResultBlueprintKey: string;
  handleBlueprintsQuizV2IndustrySubmit: (selectedOption: IndustryName) => void;
  handleBlueprintsQuizV2UseCaseSubmit: (selectedOption: UseCaseV2Name) => void;
  createBlueprintV2: (blueprintKey: string) => Promise<void>;
  closeBlueprintsQuizV2Modal: () => void;
  handleLoadBlueprintPreview: (blueprintKey: string) => Promise<void>;
  handleLoadCasesForSelectedBlueprint: (caseTemplateIds: string[]) => Promise<void>;
  handleLoadInquiryTemplateBuilderPreview: (builderName: string) => Promise<void>;
  handleLoadInquiriesForSelectedBlueprint: (inquiryTemplateIds: string[]) => Promise<void>;
  handleLoadReportsForSelectedBlueprint: (reportTemplateIds: string[]) => Promise<void>;
  handleLoadUsers: () => Promise<void>;
  handleLoadWorkflowRunsForSelectedBlueprint: (workflowIds: string[]) => Promise<void>;
  handleSaveCompletedStep: (stepKey: string) => void;
}

const DashboardSandboxHomeView = ({
  previouslyUsedBlueprints,
  blueprintsCatalogServer,
  blueprintPreviews,
  casesForSelectedBlueprint,
  currentEnvironment,
  inquiriesForSelectedBlueprint,
  inquiryTemplateBuilderPreviews,
  organization,
  currentOrganizationPlan,
  reportsForSelectedBlueprint,
  users,
  workflowRunsForSelectedBlueprint,
  isLoadingPreviouslyUsedBlueprints,
  isLoadingBlueprintsCatalog,
  isLoadingCasesForSelectedBlueprint,
  isLoadingInquiriesForSelectedBlueprint,
  isLoadingReportsForSelectedBlueprint,
  isLoadingUsers,
  isLoadingWorkflowRunsForSelectedBlueprint,
  isSavingCompletedStep,
  blueprintsQuizV2SelectedIndustry,
  blueprintsQuizV2SelectedUseCase,
  blueprintsQuizV2ResultBlueprintKey,
  handleBlueprintsQuizV2IndustrySubmit,
  handleBlueprintsQuizV2UseCaseSubmit,
  createBlueprintV2,
  closeBlueprintsQuizV2Modal,
  handleLoadBlueprintPreview,
  handleLoadCasesForSelectedBlueprint,
  handleLoadInquiryTemplateBuilderPreview,
  handleLoadInquiriesForSelectedBlueprint,
  handleLoadReportsForSelectedBlueprint,
  handleLoadUsers,
  handleLoadWorkflowRunsForSelectedBlueprint,
  handleSaveCompletedStep,
}: DashboardSandboxHomeViewProps) => {
  const [selectedModuleKey, setSelectedModuleKey] = useState('basics');
  const [selectedStepKey, setSelectedStepKey] = useState(OnboardingGuideStepKey.ChooseSolution);
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const [showBlueprintsQuizModal, setShowBlueprintsQuizModal] = useState(false);
  // This is only set to true when the user clicks Get recommendations in ChooseSolutionStep
  const [isBlueprintsQuizDismissable, setIsBlueprintsQuizDismissable] = useState(false);
  const [showBlueprintsAllModal, setShowBlueprintsAllModal] = useState(false);
  const [showBlueprintDemoModal, setShowBlueprintDemoModal] = useState(false);

  const navigate = useNavigate();

  const pendingOrganizationPlanMigration = useSelector(
    (state: RootState) => state.organization.pendingOrganizationPlanMigration,
  );

  useEffect(() => {
    handleLoadUsers();
  }, []);

  const [selectedBlueprint, setSelectedBlueprint] = useState<Blueprint | undefined>(undefined);

  useEffect(() => {
    if (selectedBlueprint) {
      const inquiryTemplateBlocks = selectedBlueprint.attributes.blocks.filter(
        (block) => block.type === BlueprintBlockType.InquiryTemplate,
      );
      if (inquiryTemplateBlocks.length > 0) {
        handleLoadInquiriesForSelectedBlueprint(inquiryTemplateBlocks.map((itmpl) => itmpl.token));
      }
      const workflowBlocks = selectedBlueprint.attributes.blocks.filter(
        (block) => block.type === BlueprintBlockType.Workflow,
      );
      if (workflowBlocks.length > 0) {
        handleLoadWorkflowRunsForSelectedBlueprint(workflowBlocks.map((wf) => wf.token));
      }
      const reportTemplateBlocks = selectedBlueprint.attributes.blocks.filter(
        (block) => block.type === BlueprintBlockType.ReportTemplate,
      );
      if (reportTemplateBlocks.length > 0) {
        handleLoadReportsForSelectedBlueprint(reportTemplateBlocks.map((rptp) => rptp.token));
      }
      const caseTemplateBlocks = selectedBlueprint.attributes.blocks.filter(
        (block) => block.type === BlueprintBlockType.CaseTemplate,
      );
      if (caseTemplateBlocks.length > 0) {
        handleLoadCasesForSelectedBlueprint(caseTemplateBlocks.map((ctmpl) => ctmpl.token));
      }
    }
  }, [selectedBlueprint]);

  useEffect(() => {
    if (!isLoadingPreviouslyUsedBlueprints && !isLoadingBlueprintsCatalog) {
      const isActivatedOrg = organization.attributes.status === OrganizationStatus.Active;

      if (
        previouslyUsedBlueprints.length === 0 &&
        !isActivatedOrg &&
        !organization.attributes['disable-required-onboarding-quiz']
      ) {
        setShowBlueprintsQuizModal(true);
      }
    }
  }, [isLoadingPreviouslyUsedBlueprints, isLoadingBlueprintsCatalog]);

  const [completedStepKeysComputed, setCompletedStepKeysComputed] = useState([]);
  // The completion status of certain steps will never be saved in completedStepKeys. Instead,
  // they are computed dynamically here.
  useEffect(() => {
    const completedStepKeys =
      organization.attributes['onboarding-checklist-sandbox-completed-steps'];

    if (selectedBlueprint) {
      if (!completedStepKeys.includes(OnboardingGuideStepKey.ChooseSolution)) {
        completedStepKeys.push(OnboardingGuideStepKey.ChooseSolution);
      }
    }

    if (users && users.length > 1) {
      if (!completedStepKeys.includes(OnboardingGuideStepKey.InviteTeamMembers)) {
        completedStepKeys.push(OnboardingGuideStepKey.InviteTeamMembers);
      }
    }

    if (inquiriesForSelectedBlueprint.length > 0) {
      if (!completedStepKeys.includes(OnboardingGuideStepKey.RunTestInquiry)) {
        completedStepKeys.push(OnboardingGuideStepKey.RunTestInquiry);
      }
    }

    if (
      organization.attributes.status !== OrganizationStatus.New ||
      !!pendingOrganizationPlanMigration
    ) {
      if (!completedStepKeys.includes(OnboardingGuideStepKey.RequestProductionAccess)) {
        completedStepKeys.push(OnboardingGuideStepKey.RequestProductionAccess);
      }
    }

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setCompletedStepKeysComputed(completedStepKeys);
  }, [
    previouslyUsedBlueprints,
    inquiriesForSelectedBlueprint,
    users,
    organization.attributes['onboarding-checklist-sandbox-completed-steps'],
  ]);

  return (
    <>
      {isLoadingPreviouslyUsedBlueprints || isLoadingBlueprintsCatalog ? (
        <LoadingIndicator />
      ) : (
        <>
          <ModalWrapper isOpen={showBlueprintsQuizModal}>
            <BlueprintsQuizV2Modal
              currentOrganizationPlan={currentOrganizationPlan}
              pendingOrganizationPlanMigration={pendingOrganizationPlanMigration}
              selectedIndustry={blueprintsQuizV2SelectedIndustry}
              selectedUseCase={blueprintsQuizV2SelectedUseCase}
              quizResultBlueprintKey={blueprintsQuizV2ResultBlueprintKey}
              blueprintsCatalogServer={blueprintsCatalogServer}
              previouslyUsedBlueprintKeys={previouslyUsedBlueprints.map(
                (blueprint) => blueprint['attributes']['source-type'],
              )}
              blueprintPreviews={blueprintPreviews}
              inquiryTemplateBuilderPreviews={inquiryTemplateBuilderPreviews}
              handleIndustrySubmit={handleBlueprintsQuizV2IndustrySubmit}
              handleUseCaseSubmit={handleBlueprintsQuizV2UseCaseSubmit}
              handleLoadBlueprintPreview={handleLoadBlueprintPreview}
              handleLoadInquiryTemplateBuilderPreview={handleLoadInquiryTemplateBuilderPreview}
              createBlueprint={createBlueprintV2}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              closeModal={
                isBlueprintsQuizDismissable ? () => setShowBlueprintsQuizModal(false) : null
              }
              closeModalOnCompletion={() => {
                setShowBlueprintsQuizModal(false);
                closeBlueprintsQuizV2Modal();
                navigate({ search: '' });
              }}
              navigateToBlueprintsAllModal={() => {
                setShowBlueprintsQuizModal(false);
                setShowBlueprintsAllModal(true);
              }}
            />
          </ModalWrapper>
          <ModalWrapper isOpen={showBlueprintsAllModal}>
            <BlueprintsAllModal
              blueprintsCatalogServer={blueprintsCatalogServer}
              blueprintPreviews={blueprintPreviews}
              inquiryTemplateBuilderPreviews={inquiryTemplateBuilderPreviews}
              previouslyUsedBlueprints={previouslyUsedBlueprints}
              organization={organization}
              currentOrganizationPlan={currentOrganizationPlan}
              handleLoadBlueprintPreview={handleLoadBlueprintPreview}
              handleLoadInquiryTemplateBuilderPreview={handleLoadInquiryTemplateBuilderPreview}
              createBlueprint={createBlueprintV2}
              closeModal={() => setShowBlueprintsAllModal(false)}
            />
          </ModalWrapper>
          {selectedBlueprint && (
            <ModalWrapper isOpen={showBlueprintDemoModal}>
              <ModalContainer size='xl'>
                <BlueprintDemoModalContents
                  blueprintKey={selectedBlueprint.attributes['source-type']}
                  blueprintsCatalogServer={blueprintsCatalogServer}
                  blueprint={selectedBlueprint}
                  blueprintPreviews={blueprintPreviews}
                  inquiryTemplateBuilderPreviews={inquiryTemplateBuilderPreviews}
                  currentEnvironment={currentEnvironment}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  subdomain={organization.attributes.subdomain}
                  isBlueprintAddedToSandbox={true}
                  handleLoadBlueprintPreview={handleLoadBlueprintPreview}
                  handleLoadInquiryTemplateBuilderPreview={handleLoadInquiryTemplateBuilderPreview}
                  closeModal={() => setShowBlueprintDemoModal(false)}
                />
              </ModalContainer>
            </ModalWrapper>
          )}
          <InactiveEnvironmentBanner />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <Container>
              <Header title='Getting Started Guide' />
              <Section>
                <LayoutWrapper>
                  <MainColumn>
                    {guideContent.map((module) => (
                      <GuideModule
                        key={module.moduleKey}
                        moduleKey={module.moduleKey}
                        moduleTitle={module.moduleTitle}
                        moduleIcon={module.moduleIcon}
                        isSelected={module.moduleKey === selectedModuleKey}
                        steps={module.steps}
                        completedStepKeys={completedStepKeysComputed}
                        isSavingCompletedStep={isSavingCompletedStep}
                        selectedStepKey={selectedStepKey}
                        blueprintsCatalogServer={blueprintsCatalogServer}
                        previouslyUsedBlueprints={previouslyUsedBlueprints}
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        selectedBlueprint={selectedBlueprint}
                        currentEnvironment={currentEnvironment}
                        casesForSelectedBlueprint={casesForSelectedBlueprint}
                        isLoadingCasesForSelectedBlueprint={isLoadingCasesForSelectedBlueprint}
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        selectedInquiry={selectedInquiry}
                        inquiriesForSelectedBlueprint={inquiriesForSelectedBlueprint}
                        isLoadingInquiriesForSelectedBlueprint={
                          isLoadingInquiriesForSelectedBlueprint
                        }
                        inquiryTemplateBuilderPreviews={inquiryTemplateBuilderPreviews}
                        organization={organization}
                        reportsForSelectedBlueprint={reportsForSelectedBlueprint}
                        isLoadingReportsForSelectedBlueprint={isLoadingReportsForSelectedBlueprint}
                        isLoadingUsers={isLoadingUsers}
                        workflowRunsForSelectedBlueprint={workflowRunsForSelectedBlueprint}
                        isLoadingWorkflowRunsForSelectedBlueprint={
                          isLoadingWorkflowRunsForSelectedBlueprint
                        }
                        blueprintPreviews={blueprintPreviews}
                        isActivatedOrg={
                          organization.attributes.status === OrganizationStatus.Active
                        }
                        setSelectedModuleKey={setSelectedModuleKey}
                        setSelectedStepKey={setSelectedStepKey}
                        setSelectedBlueprint={setSelectedBlueprint}
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        setSelectedInquiry={setSelectedInquiry}
                        openBlueprintsQuizModal={() => {
                          setIsBlueprintsQuizDismissable(true);
                          setShowBlueprintsQuizModal(true);
                        }}
                        handleLoadCasesForSelectedBlueprint={handleLoadCasesForSelectedBlueprint}
                        handleLoadInquiriesForSelectedBlueprint={
                          handleLoadInquiriesForSelectedBlueprint
                        }
                        handleLoadReportsForSelectedBlueprint={
                          handleLoadReportsForSelectedBlueprint
                        }
                        handleLoadUsers={handleLoadUsers}
                        handleLoadWorkflowRunsForSelectedBlueprint={
                          handleLoadWorkflowRunsForSelectedBlueprint
                        }
                        handleLoadBlueprintPreview={handleLoadBlueprintPreview}
                        handleLoadInquiryTemplateBuilderPreview={
                          handleLoadInquiryTemplateBuilderPreview
                        }
                        handleSaveCompletedStep={handleSaveCompletedStep}
                      />
                    ))}
                  </MainColumn>
                  <NarrowColumn>
                    <Card>
                      <Card.Body>
                        <Heading size='sm'>Explore all solutions</Heading>
                        <Paragraph color='primary'>
                          Browse all of our pre-built solutions that use our most popular building
                          blocks.
                        </Paragraph>
                        <Button
                          id='home-sandbox-onboarding-checklist__all-blueprints'
                          variant='basic'
                          width='button'
                          suffixIcon={<ArrowRightIcon />}
                          onClick={() => setShowBlueprintsAllModal(true)}
                        >
                          Browse
                        </Button>
                      </Card.Body>
                    </Card>
                    <Card>
                      <Card.Body>
                        <Heading size='sm'>Additional resources</Heading>
                        <Link to='/dashboard/getting-started'>Getting Started</Link>
                        <a href='https://help.withpersona.com' target='_blank' rel='noreferrer'>
                          Help Center <LinkExternalIcon />
                        </a>
                        <a
                          href='https://docs.withpersona.com/docs'
                          target='_blank'
                          rel='noreferrer'
                        >
                          Developer documentation <LinkExternalIcon />
                        </a>
                        <a
                          href='https://withpersona.com/community-beta'
                          target='_blank'
                          rel='noreferrer'
                        >
                          Join the Persona community <LinkExternalIcon />
                        </a>
                        <a href='https://academy.withpersona.com' target='_blank' rel='noreferrer'>
                          Persona Academy <LinkExternalIcon />
                        </a>
                      </Card.Body>
                    </Card>
                  </NarrowColumn>
                </LayoutWrapper>
              </Section>
            </Container>
          </motion.div>
        </>
      )}
    </>
  );
};

interface GuideModuleProps {
  moduleKey: string;
  moduleTitle: string;
  moduleIcon: ReactNode;
  isSelected: boolean;
  steps: OnboardingGuideStep[];
  completedStepKeys: string[];
  isSavingCompletedStep: boolean;
  selectedStepKey: string;
  blueprintsCatalogServer: BlueprintServerMetadata[];
  previouslyUsedBlueprints: Blueprint[];
  selectedBlueprint: Blueprint;
  casesForSelectedBlueprint: Case[];
  isLoadingCasesForSelectedBlueprint: boolean;
  currentEnvironment: Environment;
  selectedInquiry: Inquiry;
  inquiriesForSelectedBlueprint: Inquiry[];
  inquiryTemplateBuilderPreviews: BuilderPreviewMap;
  isLoadingInquiriesForSelectedBlueprint: boolean;
  organization: Organization;
  reportsForSelectedBlueprint: Report[];
  isLoadingReportsForSelectedBlueprint: boolean;
  isLoadingUsers: boolean;
  workflowRunsForSelectedBlueprint: WorkflowRun[];
  isLoadingWorkflowRunsForSelectedBlueprint: boolean;
  blueprintPreviews: BlueprintPreviewMap;
  isActivatedOrg: boolean;
  setSelectedModuleKey: (moduleKey: string) => void;
  setSelectedStepKey: (stepKey: OnboardingGuideStepKey) => void;
  setSelectedBlueprint: (blueprint: Blueprint) => void;
  setSelectedInquiry: (inquiry: Inquiry) => void;
  openBlueprintsQuizModal: () => void;
  handleLoadCasesForSelectedBlueprint: (caseTemplateIds: string[]) => Promise<void>;
  handleLoadInquiriesForSelectedBlueprint: (inquiryTemplateIds: string[]) => Promise<void>;
  handleLoadReportsForSelectedBlueprint: (reportTemplateIds: string[]) => Promise<void>;
  handleLoadUsers: () => Promise<void>;
  handleLoadWorkflowRunsForSelectedBlueprint: (workflowIds: string[]) => Promise<void>;
  handleLoadBlueprintPreview: (blueprintKey: string) => Promise<void>;
  handleLoadInquiryTemplateBuilderPreview: (builderName: string) => Promise<void>;
  handleSaveCompletedStep: (stepKey: string) => void;
}

const GuideModule = ({
  moduleKey,
  moduleTitle,
  moduleIcon,
  isSelected,
  steps,
  completedStepKeys,
  isSavingCompletedStep,
  selectedStepKey,
  blueprintsCatalogServer,
  previouslyUsedBlueprints,
  selectedBlueprint,
  casesForSelectedBlueprint,
  isLoadingCasesForSelectedBlueprint,
  currentEnvironment,
  selectedInquiry,
  inquiriesForSelectedBlueprint,
  isLoadingInquiriesForSelectedBlueprint,
  inquiryTemplateBuilderPreviews,
  organization,
  reportsForSelectedBlueprint,
  isLoadingReportsForSelectedBlueprint,
  isLoadingUsers,
  workflowRunsForSelectedBlueprint,
  isLoadingWorkflowRunsForSelectedBlueprint,
  blueprintPreviews,
  isActivatedOrg,
  setSelectedModuleKey,
  setSelectedStepKey,
  setSelectedBlueprint,
  setSelectedInquiry,
  openBlueprintsQuizModal,
  handleLoadCasesForSelectedBlueprint,
  handleLoadInquiriesForSelectedBlueprint,
  handleLoadReportsForSelectedBlueprint,
  handleLoadUsers,
  handleLoadWorkflowRunsForSelectedBlueprint,
  handleLoadBlueprintPreview,
  handleLoadInquiryTemplateBuilderPreview,
  handleSaveCompletedStep,
}: GuideModuleProps) => {
  const modulesDisabledIfNoSelectedBlueprint = [
    OnboardingGuideModuleKey.TestSolution,
    OnboardingGuideModuleKey.RefineSolution,
    OnboardingGuideModuleKey.Integration,
    OnboardingGuideModuleKey.ManageUserLifecycle,
  ];
  const isDisabled =
    !selectedBlueprint && (modulesDisabledIfNoSelectedBlueprint as string[]).includes(moduleKey);

  // Remove any steps that should be hidden due to the selectedBlueprint not having
  // the requiredBlueprintBlockType for that step
  let computedSteps = steps;
  if (selectedBlueprint) {
    const selectedBlueprintBlockTypes = selectedBlueprint.attributes.blocks.map(
      (block) => block.type,
    );
    computedSteps = steps.filter(
      (step) =>
        !step.requiredBlueprintBlockType ||
        (step.requiredBlueprintBlockType &&
          selectedBlueprintBlockTypes.includes(step.requiredBlueprintBlockType)),
    );
  }

  const moduleStepKeys = computedSteps.map((step) => step.key as string);
  const moduleIncompleteStepKeys = moduleStepKeys.filter((key) => !completedStepKeys.includes(key));
  const numIncompleteSteps = moduleIncompleteStepKeys.length;

  const moduleIncompleteMinutesRemaining = computedSteps
    .filter((step) => moduleIncompleteStepKeys.includes(step.key))
    .map((step) => step.minutesToComplete);
  const minutesRemaining = moduleIncompleteMinutesRemaining.reduce((a, b) => a + b, 0);

  if (isDisabled) {
    return (
      <Tooltip
        tooltip='Choose your solution in the first step in order to unlock these modules!'
        hideArrow={false}
        placement='bottom'
      >
        <GuideModuleWrapper isSelected={isSelected} isDisabled={true}>
          <GuideModuleHeaderWrapper>
            <GuideModuleHeader>
              <GuideModuleTitle>
                {moduleIcon}
                {moduleTitle}
              </GuideModuleTitle>
              <GuideModuleStatus>
                {numIncompleteSteps} {pluralize('step', numIncompleteSteps)} left &#183; About{' '}
                {minutesRemaining} min
              </GuideModuleStatus>
            </GuideModuleHeader>
          </GuideModuleHeaderWrapper>
        </GuideModuleWrapper>
      </Tooltip>
    );
  }

  return (
    <GuideModuleWrapper isSelected={isSelected} isDisabled={false}>
      <GuideModuleHeaderWrapper
        onClick={() => {
          if (isSelected) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setSelectedModuleKey(null);
          } else {
            setSelectedModuleKey(moduleKey);
          }
        }}
      >
        <GuideModuleHeader>
          <GuideModuleTitle>
            {moduleIcon}
            {moduleKey === OnboardingGuideModuleKey.TestSolution ||
            moduleKey === OnboardingGuideModuleKey.RefineSolution ? (
              <>
                {moduleTitle}: {selectedBlueprint.attributes.name}
              </>
            ) : (
              moduleTitle
            )}
          </GuideModuleTitle>
          <GuideModuleStatus>
            {numIncompleteSteps} {pluralize('step', numIncompleteSteps)} left &#183; About{' '}
            {minutesRemaining} min
          </GuideModuleStatus>
        </GuideModuleHeader>
        <GuideModuleProgressBar
          percentComplete={(1 - numIncompleteSteps / computedSteps.length) * 100}
        />
      </GuideModuleHeaderWrapper>
      {isSelected && (
        <GuideSteps>
          {computedSteps.map((step, index) => (
            <GuideStep
              key={step.key}
              step={step}
              isCompleted={completedStepKeys.includes(step.key)}
              isSavingCompletedStep={isSavingCompletedStep}
              index={index + 1}
              isSelected={selectedStepKey === step.key}
              blueprintsCatalogServer={blueprintsCatalogServer}
              previouslyUsedBlueprints={previouslyUsedBlueprints}
              selectedBlueprint={selectedBlueprint}
              casesForSelectedBlueprint={casesForSelectedBlueprint}
              isLoadingCasesForSelectedBlueprint={isLoadingCasesForSelectedBlueprint}
              currentEnvironment={currentEnvironment}
              selectedInquiry={selectedInquiry}
              inquiriesForSelectedBlueprint={inquiriesForSelectedBlueprint}
              isLoadingInquiriesForSelectedBlueprint={isLoadingInquiriesForSelectedBlueprint}
              inquiryTemplateBuilderPreviews={inquiryTemplateBuilderPreviews}
              organization={organization}
              reportsForSelectedBlueprint={reportsForSelectedBlueprint}
              isLoadingReportsForSelectedBlueprint={isLoadingReportsForSelectedBlueprint}
              isLoadingUsers={isLoadingUsers}
              workflowRunsForSelectedBlueprint={workflowRunsForSelectedBlueprint}
              isLoadingWorkflowRunsForSelectedBlueprint={isLoadingWorkflowRunsForSelectedBlueprint}
              blueprintPreviews={blueprintPreviews}
              isActivatedOrg={isActivatedOrg}
              setSelectedStepKey={setSelectedStepKey}
              setSelectedBlueprint={setSelectedBlueprint}
              setSelectedInquiry={setSelectedInquiry}
              openBlueprintsQuizModal={openBlueprintsQuizModal}
              handleLoadCasesForSelectedBlueprint={handleLoadCasesForSelectedBlueprint}
              handleLoadInquiriesForSelectedBlueprint={handleLoadInquiriesForSelectedBlueprint}
              handleLoadReportsForSelectedBlueprint={handleLoadReportsForSelectedBlueprint}
              handleLoadUsers={handleLoadUsers}
              handleLoadWorkflowRunsForSelectedBlueprint={
                handleLoadWorkflowRunsForSelectedBlueprint
              }
              handleLoadBlueprintPreview={handleLoadBlueprintPreview}
              handleLoadInquiryTemplateBuilderPreview={handleLoadInquiryTemplateBuilderPreview}
              handleSaveCompletedStep={handleSaveCompletedStep}
            />
          ))}
        </GuideSteps>
      )}
    </GuideModuleWrapper>
  );
};

const GuideModuleProgressBar = ({ percentComplete }: { percentComplete: number }) => (
  <GuideModuleProgressBarWrapper>
    <ProgressBarComplete widthPercentage={percentComplete} />
    <ProgressBarIncomplete widthPercentage={100 - percentComplete} />
  </GuideModuleProgressBarWrapper>
);

interface GuideStepProps {
  step: OnboardingGuideStep;
  isCompleted: boolean;
  isSavingCompletedStep: boolean;
  index: number;
  isSelected: boolean;
  blueprintsCatalogServer: BlueprintServerMetadata[];
  previouslyUsedBlueprints: Blueprint[];
  selectedBlueprint: Blueprint;
  casesForSelectedBlueprint: Case[];
  isLoadingCasesForSelectedBlueprint: boolean;
  currentEnvironment: Environment;
  selectedInquiry: Inquiry;
  inquiriesForSelectedBlueprint: Inquiry[];
  isLoadingInquiriesForSelectedBlueprint: boolean;
  inquiryTemplateBuilderPreviews: BuilderPreviewMap;
  organization: Organization;
  reportsForSelectedBlueprint: Report[];
  isLoadingReportsForSelectedBlueprint: boolean;
  isLoadingUsers: boolean;
  workflowRunsForSelectedBlueprint: WorkflowRun[];
  isLoadingWorkflowRunsForSelectedBlueprint: boolean;
  blueprintPreviews: BlueprintPreviewMap;
  isActivatedOrg: boolean;
  setSelectedStepKey: (stepKey: OnboardingGuideStepKey) => void;
  setSelectedBlueprint: (blueprint: Blueprint) => void;
  setSelectedInquiry: (inquiry: Inquiry) => void;
  openBlueprintsQuizModal: () => void;
  handleLoadCasesForSelectedBlueprint: (caseTemplateIds: string[]) => Promise<void>;
  handleLoadInquiriesForSelectedBlueprint: (inquiryTemplateIds: string[]) => Promise<void>;
  handleLoadReportsForSelectedBlueprint: (reportTemplateIds: string[]) => Promise<void>;
  handleLoadUsers: () => Promise<void>;
  handleLoadWorkflowRunsForSelectedBlueprint: (workflowIds: string[]) => Promise<void>;
  handleLoadBlueprintPreview: (blueprintKey: string) => Promise<void>;
  handleLoadInquiryTemplateBuilderPreview: (builderName: string) => Promise<void>;
  handleSaveCompletedStep: (stepKey: string) => void;
}

const GuideStep = ({
  step,
  isCompleted,
  isSavingCompletedStep,
  index,
  isSelected,
  blueprintsCatalogServer,
  previouslyUsedBlueprints,
  selectedBlueprint,
  casesForSelectedBlueprint,
  isLoadingCasesForSelectedBlueprint,
  currentEnvironment,
  selectedInquiry,
  inquiriesForSelectedBlueprint,
  isLoadingInquiriesForSelectedBlueprint,
  inquiryTemplateBuilderPreviews,
  organization,
  reportsForSelectedBlueprint,
  isLoadingReportsForSelectedBlueprint,
  isLoadingUsers,
  workflowRunsForSelectedBlueprint,
  isLoadingWorkflowRunsForSelectedBlueprint,
  blueprintPreviews,
  isActivatedOrg,
  setSelectedStepKey,
  setSelectedBlueprint,
  setSelectedInquiry,
  openBlueprintsQuizModal,
  handleLoadCasesForSelectedBlueprint,
  handleLoadInquiriesForSelectedBlueprint,
  handleLoadReportsForSelectedBlueprint,
  handleLoadUsers,
  handleLoadWorkflowRunsForSelectedBlueprint,
  handleLoadBlueprintPreview,
  handleLoadInquiryTemplateBuilderPreview,
  handleSaveCompletedStep,
}: GuideStepProps) => {
  // Inserts template tokens into DashboardPageLinks
  const parseButtonLink = (url: DashboardPageLink) => {
    let transformedUrl = url as string;

    if (
      url.includes('[inquiry-template-token]') ||
      url.includes('[inquiry-template-version-token]')
    ) {
      const inquiryTemplateBlocks = selectedBlueprint.attributes.blocks.filter(
        (block) => block.type === BlueprintBlockType.InquiryTemplate,
      );
      if (inquiryTemplateBlocks.length > 0) {
        const inquiryTemplateToken = inquiryTemplateBlocks[0].token;
        const inquiryTemplateVersionToken =
          inquiryTemplateBlocks[0]['latest-published-version-token'];
        transformedUrl = transformedUrl.replace('[inquiry-template-token]', inquiryTemplateToken);
        transformedUrl = transformedUrl.replace(
          '[inquiry-template-version-token]',
          inquiryTemplateVersionToken,
        );
      }
    }

    if (url.includes('[workflow-token]') || url.includes('[workflow-version-token]')) {
      const workflowBlocks = selectedBlueprint.attributes.blocks.filter(
        (block) => block.type === BlueprintBlockType.Workflow,
      );
      if (workflowBlocks.length > 0) {
        const workflowToken = workflowBlocks[0].token;
        const workflowVersionToken = workflowBlocks[0]['latest-published-version-token'];
        transformedUrl = transformedUrl.replace('[workflow-token]', workflowToken);
        transformedUrl = transformedUrl.replace('[workflow-version-token]', workflowVersionToken);
      }
    }

    return transformedUrl;
  };

  return (
    <GuideStepWrapper isSelected={isSelected}>
      <GuideStepHeader
        onClick={(e) => {
          e.stopPropagation();
          if (isSelected) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            setSelectedStepKey(null);
          } else {
            setSelectedStepKey(step.key);
          }
        }}
      >
        <GuideStepTitle>
          {isCompleted ? (
            <GuideStepCheckmark isSelected={isSelected}>
              <CheckIcon color={Color.PrimaryWhite} />
            </GuideStepCheckmark>
          ) : (
            <GuideStepIndex>{index}</GuideStepIndex>
          )}
          {step.title}
        </GuideStepTitle>
        {isSelected ? <ChevronDownIcon /> : <ChevronRightIcon />}
      </GuideStepHeader>
      {isSelected && (
        <GuideStepContent>
          {isCompleted && (
            <GuideStepCompletedBanner>
              You’re done! Come back anytime to refresh your memory.
            </GuideStepCompletedBanner>
          )}
          {/* Steps that cannot be rendered with static markup use their own component, hence customComponentName */}
          {step.customComponentName ? (
            <>
              {step.customComponentName === 'ChooseSolutionStep' && (
                <ChooseSolutionStep
                  blueprintsCatalogServer={blueprintsCatalogServer}
                  previouslyUsedBlueprints={previouslyUsedBlueprints}
                  selectedBlueprint={selectedBlueprint}
                  setSelectedBlueprint={setSelectedBlueprint}
                  openBlueprintsQuizModal={openBlueprintsQuizModal}
                />
              )}
              {step.customComponentName === 'InviteTeamMembersStep' && (
                <InviteTeamMembersStep
                  isStepCompleted={isCompleted}
                  isLoadingUsers={isLoadingUsers}
                  handleLoadUsers={handleLoadUsers}
                />
              )}
              {step.customComponentName === 'PreviewSolutionStep' && (
                <PreviewSolutionStep
                  blueprintKey={selectedBlueprint.attributes['source-type']}
                  blueprintPreviews={blueprintPreviews}
                  inquiryTemplateBuilderPreviews={inquiryTemplateBuilderPreviews}
                  isStepCompleted={isCompleted}
                  isSavingCompletedStep={isSavingCompletedStep}
                  handleLoadBlueprintPreview={handleLoadBlueprintPreview}
                  handleLoadInquiryTemplateBuilderPreview={handleLoadInquiryTemplateBuilderPreview}
                  saveCompletedStep={() => handleSaveCompletedStep(step.key)}
                />
              )}
              {step.customComponentName === 'RunTestInquiryStep' && (
                <RunTestInquiryStep
                  currentEnvironment={currentEnvironment}
                  organization={organization}
                  selectedBlueprint={selectedBlueprint}
                  isStepCompleted={isCompleted}
                  isLoadingInquiriesForSelectedBlueprint={isLoadingInquiriesForSelectedBlueprint}
                  handleLoadInquiriesForSelectedBlueprint={handleLoadInquiriesForSelectedBlueprint}
                />
              )}
              {step.customComponentName === 'ReviewCompletedInquiryStep' && (
                <ReviewCompletedInquiryStep
                  inquiriesForSelectedBlueprint={inquiriesForSelectedBlueprint}
                  selectedInquiry={selectedInquiry}
                  selectedBlueprint={selectedBlueprint}
                  isLoadingInquiriesForSelectedBlueprint={isLoadingInquiriesForSelectedBlueprint}
                  isStepCompleted={isCompleted}
                  isSavingCompletedStep={isSavingCompletedStep}
                  handleLoadInquiriesForSelectedBlueprint={handleLoadInquiriesForSelectedBlueprint}
                  setSelectedInquiry={setSelectedInquiry}
                  saveCompletedStep={() => handleSaveCompletedStep(step.key)}
                />
              )}
              {step.customComponentName === 'ReviewWorkflowRunStep' && (
                <ReviewWorkflowRunStep
                  workflowRunsForSelectedBlueprint={workflowRunsForSelectedBlueprint}
                  isLoadingWorkflowRunsForSelectedBlueprint={
                    isLoadingWorkflowRunsForSelectedBlueprint
                  }
                  selectedBlueprint={selectedBlueprint}
                  isStepCompleted={isCompleted}
                  isSavingCompletedStep={isSavingCompletedStep}
                  handleLoadWorkflowRunsForSelectedBlueprint={
                    handleLoadWorkflowRunsForSelectedBlueprint
                  }
                  saveCompletedStep={() => handleSaveCompletedStep(step.key)}
                />
              )}
              {step.customComponentName === 'ReviewReportResultsStep' && (
                <ReviewReportResultsStep
                  reportsForSelectedBlueprint={reportsForSelectedBlueprint}
                  isLoadingReportsForSelectedBlueprint={isLoadingReportsForSelectedBlueprint}
                  selectedBlueprint={selectedBlueprint}
                  isStepCompleted={isCompleted}
                  isSavingCompletedStep={isSavingCompletedStep}
                  handleLoadReportsForSelectedBlueprint={handleLoadReportsForSelectedBlueprint}
                  saveCompletedStep={() => handleSaveCompletedStep(step.key)}
                />
              )}
              {step.customComponentName === 'ReviewCaseStep' && (
                <ReviewCaseStep
                  casesForSelectedBlueprint={casesForSelectedBlueprint}
                  isLoadingCasesForSelectedBlueprint={isLoadingCasesForSelectedBlueprint}
                  selectedBlueprint={selectedBlueprint}
                  isStepCompleted={isCompleted}
                  isSavingCompletedStep={isSavingCompletedStep}
                  handleLoadCasesForSelectedBlueprint={handleLoadCasesForSelectedBlueprint}
                  saveCompletedStep={() => handleSaveCompletedStep(step.key)}
                />
              )}
              {step.customComponentName === 'ChooseIntegrationMethodStep' && (
                <ChooseIntegrationMethodStep
                  isStepCompleted={isCompleted}
                  isSavingCompletedStep={isSavingCompletedStep}
                  saveCompletedStep={() => handleSaveCompletedStep(step.key)}
                />
              )}
              {step.customComponentName === 'RequestProductionAccessStep' && (
                <RequestProductionAccessStep isStepCompleted={isCompleted} />
              )}
              {step.customComponentName === 'RunTestInquiryInProductionStep' && (
                <RunTestInquiryInProductionStep
                  isActivatedOrg={isActivatedOrg}
                  isStepCompleted={isCompleted}
                  isSavingCompletedStep={isSavingCompletedStep}
                  saveCompletedStep={() => handleSaveCompletedStep(step.key)}
                />
              )}
            </>
          ) : (
            <>
              {step.content}
              <Spacer height={2} />
              {isSavingCompletedStep ? (
                <Button disabled width='button'>
                  Saving...
                </Button>
              ) : (
                <>
                  {step.buttonText && ((isCompleted && step.buttonLink) || !isCompleted) && (
                    <Button
                      id={`home-sandbox-onboarding-checklist__completed-step-${step.key}`}
                      width='button'
                      onClick={() => {
                        if (!isCompleted && step.buttonClickCompletesStep) {
                          handleSaveCompletedStep(step.key);
                        }
                        if (step.buttonLink) {
                          window.open(parseButtonLink(step.buttonLink));
                        }
                      }}
                    >
                      {step.buttonText}
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </GuideStepContent>
      )}
    </GuideStepWrapper>
  );
};

const LayoutWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: ${Spacing[5]};
`;

const MainColumn = styled('div')`
  flex: 2;
  min-width: 400px;
`;

const NarrowColumn = styled('div')`
  flex: 1;
  min-width: 300px;
`;

const GuideModuleHeaderWrapper = styled('div')``;

const GuideModuleHeader = styled('div')`
  padding: ${Spacing[4]};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  font-size: 14px;
  cursor: pointer;
`;

const GuideModuleProgressBarWrapper = styled('div')`
  display: flex;
  flex-direction: row;
`;

const ProgressBarComplete = styled('div')<{ widthPercentage: number }>`
  width: ${({ widthPercentage }) => `${widthPercentage}%`};
  height: 3px;
  background-color: ${Color.CoreGreen700};
`;

const ProgressBarIncomplete = styled('div')<{ widthPercentage: number }>`
  width: ${({ widthPercentage }) => `${widthPercentage}%`};
  height: 3px;
  background-color: ${Color.CoreSlate300};
`;

const GuideModuleTitle = styled('div')`
  display: flex;
  font-size: 18px;
  font-weight: 500;
  align-items: center;

  > svg {
    margin-right: ${Spacing[2]};
  }
`;

const GuideModuleStatus = styled('div')`
  text-align: right;
`;

const GuideModuleWrapper = styled(Card)<{ isSelected: boolean; isDisabled: boolean }>`
  border: 1px solid
    ${({ isSelected }) => (isSelected ? `${Color.CorePurple700}` : `${Color.CoreSlate300}`)};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  transition: ${TransitionDuration.Medium};

  ${({ isDisabled }) =>
    !isDisabled &&
    css`
      &:hover {
        border: 1px solid ${Color.CorePurple700};
      }
    `}

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      color: ${Color.CoreSlate700};

      ${GuideModuleHeader} {
        cursor: auto;
      }
    `}

  ${({ isSelected }) =>
    !isSelected &&
    css`
      ${GuideModuleHeaderWrapper} {
        border-radius: 0px 0px 4px 4px;
      }
    `}
`;

const GuideSteps = styled('div')`
  margin: ${Spacing[2]} 0px;
`;

const GuideStepHeader = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing[2]} ${Spacing[4]};
  cursor: pointer;
`;

const GuideStepTitle = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-weight: 500;
  font-size: 16px;
`;

const GuideStepIndex = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  margin-right: ${Spacing[4]};
  border: 1px solid ${Color.CoreSlate1000};
  border-radius: 50%;
  font-size: 14px;
  font-weight: normal;
  transition: ${TransitionDuration.Medium};
`;

const GuideStepCheckmark = styled('div')<{ isSelected: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  margin-right: ${Spacing[4]};
  border-radius: 50%;
  background-color: ${({ isSelected }) =>
    isSelected ? `${Color.CoreGreen700}` : `${Color.CoreGreen400}`};
  transition: ${TransitionDuration.Medium};
`;

const GuideStepWrapper = styled('div')<{ isSelected: boolean }>`
  margin: ${Spacing[2]} ${Spacing[4]};
  border: 1px solid ${({ isSelected }) => (isSelected ? `${Color.CorePurple700}` : 'transparent')};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  transition: ${TransitionDuration.Medium};

  &:hover {
    border: 1px solid ${Color.CorePurple700};
  }

  &:hover ${GuideStepHeader} {
    color: ${Color.CorePurple700};
  }

  &:hover ${GuideStepIndex} {
    border: 1px solid ${Color.CorePurple700};
  }

  &:hover ${GuideStepCheckmark} {
    background-color: ${Color.CoreGreen700};
  }
`;

const GuideStepContent = styled('div')`
  margin: ${Spacing[2]} ${Spacing[4]} ${Spacing[4]} ${Spacing[9]};
  font-size: 14px;
`;

const GuideStepCompletedBanner = styled('div')`
  margin-bottom: ${Spacing[4]};
  padding: ${Spacing[1]} ${Spacing[4]};
  background-color: ${Color.CoreGreen200};
  color: ${Color.CoreGreen800};
  font-weight: 500;
  border-radius: ${({ theme }) => theme.card.borderRadius};
`;

export default DashboardSandboxHomeView;
